import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { AppTextService } from '@fi-shared/app-text';
import { IFiDialogOptions, IFiDialogResult, IFiDialogResultValue } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiDialogsContainerBase } from '@fi-shared/fi-dialogs/fi-dialogs-container-base';
import { take } from 'rxjs/operators';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';
import { FiDialogsCommonTwoButtonsComponent } from '@fi-shared/fi-dialogs/common-two-buttons/common-two-buttons.component';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services/detect-devices.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { Observable, Subject } from 'rxjs';
import { FiUserSettingType } from '@fi-shared/types/fi-user-setting-type';

@Injectable()
export class FiDialogsService {
    constructor(
        protected matDialog: MatDialog,
        protected appTextService: AppTextService,
        protected detectDevices: DetectDevicesService,
        private fiBackend: FiBackendService,
        private userDataService: FiUserDataService,
    ) {}

    private _closeDialog$ = new Subject<void>();
    onTransitionCloseDialog$(): Observable<void> {
        return this._closeDialog$.asObservable();
    }
    triggerTransitionCloseDialog$() {
        this._closeDialog$.next();
    }

    onboarding<TData, TRes>(data: IFiDialogOptions<TData, TRes>) {
        const baseOptions: IFiDialogOptions<TData, TRes> = {
            topTitleText: this.appTextService.get('notify_dialog.default_title'),
            topTitleIconClass: 'fas fa-lightbulb',
            colorClass: 'blue',
        };
        return this.openInternal(FiDialogsCommonTwoButtonsComponent, Object.assign(baseOptions, data));
    }

    notify<TData, TRes>(data: IFiDialogOptions<TData, TRes>) {
        if (!data.yesButtonText) data.yesButtonText = this.appTextService.get('ok');
        // return this.openInternal(FiDialogsNotifyComponent, data);
        return this.openInternal<TData, TRes>(FiDialogsCommonTwoButtonsComponent, data);
    }

    blueInfoDialog<TData, TRes>(data: IFiDialogOptions<TData, TRes>) {
        const defaultDialogData: IFiDialogOptions<TData, TRes> = {
            colorClass: 'blue',
            isTopCloseButtonShown: true,
            headerIconClass: 'blue far fa-circle-exclamation',
            yesButtonText: this.appTextService.get('ok'),
            isNoButtonHidden: true,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
            },
        };
        return this.confirm(Object.assign(defaultDialogData, data));
    }

    warningConfirm<TData, TRes>(data: IFiDialogOptions<TData, TRes>) {
        const defaultDialogData: IFiDialogOptions<TData, TRes> = {
            colorClass: 'orange',
            isTopCloseButtonShown: true,
            headerIconClass: 'orange far fa-circle-exclamation',
            yesButtonText: this.appTextService.get('common.confirm'),
            noButtonText: this.appTextService.get('common.cancel'),
            isNoButtonPrimaryColor: true,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
            },
        };
        return this.confirm(Object.assign(defaultDialogData, data));
    }

    confirmSimpleDelete<TData, TRes>(data: IFiDialogOptions<TData, TRes>) {
        const defaultDialogData: IFiDialogOptions<TData, TRes> = {
            headerIconClass: 'far fa-trash red',
            isTopCloseButtonShown: true,
            colorClass: 'red',
            yesButtonText: this.appTextService.get('common.delete'),
            noButtonText: this.appTextService.get('common.cancel'),
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-confirm-delete-dialog-panel'],
                autoFocus: false,
            },
        };

        return this.confirm(Object.assign(defaultDialogData, data));
    }

    confirm<TData, TRes>(data: IFiDialogOptions<TData, TRes>) {
        const baseOptions: IFiDialogOptions<TData, TRes> = {
            yesButtonText: this.appTextService.get('yes'),
            noButtonText: this.appTextService.get('common.cancel'),
        };
        return this.openInternal(FiDialogsCommonTwoButtonsComponent, Object.assign(baseOptions, data));
    }

    error<TData, TRes>(data: IFiDialogOptions<TData, TRes> = {}) {
        let yesButtonText = this.appTextService.get('ok');
        /*if (this.appTextService.get('error_dialog.dialog_title') == 'error_dialog.dialog_title' && !data.messageText) {
            // we have error on start, no labels are loaded at this moment
            data.headerText = 'Error message';
            data.messageText = 'Something went wrong, please try again in a few minutes. If the error still exist, please contact support.';
            //data.topTitleText = 'Uh, oh...';
            yesButtonText = 'CLOSE';
        }*/

        const baseOptions: IFiDialogOptions<TData, TRes> = {
            headerText: this.appTextService.get('error_message.default_header'),
            messageText: this.appTextService.get('error_message.default_error'),
            //topTitleText: this.appTextService.get('error_dialog.dialog_title'),
            //topTitleIconClass: 'fas fa-sad-cry',
            headerIconClass: 'red far fa-circle-exclamation',
            colorClass: 'red',
            isTopCloseButtonShown: true,
            yesButtonText: yesButtonText,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                closeOnNavigation: false,
            },
        };
        const dialogOptions: IFiDialogOptions<TData, TRes> = Object.assign(baseOptions, data);
        if (dialogOptions.headerText === 'error_message.default_header') {
            dialogOptions.headerText = 'Oops! Something went wrong.';
            dialogOptions.messageText =
                'Something went wrong, please try again in a few minutes. If the error still exist, please contact support.';
            dialogOptions.yesButtonText = 'Ok';
        }

        return this.openInternal(FiDialogsCommonTwoButtonsComponent, dialogOptions);
    }

    // don't use this method without good reason, use instead custom, todo refactor current usages
    open<T, D>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>, config?: MatDialogConfig): MatDialogRef<T> {
        return this.matDialog.open<T, D>(componentOrTemplateRef, config);
    }

    getCanCloseConfirmation<TData, TRes>(
        canCloseCondition: boolean,
        dialogData?: IFiDialogOptions<TData, TRes>,
    ): Promise<{ canClose: boolean }> {
        return new Promise<{ canClose: boolean }>((resolve, reject) => {
            if (canCloseCondition) {
                resolve({ canClose: true });
            } else {
                this.discardChangesDialog(
                    () => resolve({ canClose: true }),
                    () => resolve({ canClose: false }),
                    dialogData,
                );
            }
        });
    }

    discardChangesDialog<TData, TRes>(
        onYesButtonFn: () => void,
        onNoButtonFn?: () => void,
        dialogData?: IFiDialogOptions<TData, TRes>,
    ) {
        const defaultDiscardChangesDialogData: IFiDialogOptions<TData, TRes> = {
            headerText: this.appTextService.get('common.discard_changes_header'),
            messageText: this.appTextService.get('common.leave_page_without_saving_warning_message'),
            yesButtonText: this.appTextService.get('common.discard_changes'),
            isNoButtonPrimaryColor: true,
            isBigSizeTopBorder: true,
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-dialog-no-button-primary-red'],
                autoFocus: false,
            },
        };
        this.confirmSimpleDelete(Object.assign({}, defaultDiscardChangesDialogData, dialogData))
            .onYesButtonClick(() => onYesButtonFn())
            .onNoButtonClick(() => onNoButtonFn?.());
    }

    openAnimatedBottomDialog<TData, TRes>(options: IFiDialogOptions<TData, TRes>): IFiDialogResult<TRes> {
        if (!options.yesButtonText) {
            options.yesButtonText = this.appTextService.get('ok');
        }
        if (!options.noButtonText) {
            options.noButtonText = this.appTextService.get('common.cancel');
        }
        if (!options.config) {
            options.config = {};
        }
        options.closeDelayMS = 500;

        let panelClasses: string[] = ['dialog-bottom-panel', 'dialog-animated-open', 'dialog-full-content'];
        if (typeof options.config.panelClass === 'string') {
            panelClasses.push(options.config.panelClass);
        } else if (Array.isArray(options.config.panelClass)) {
            panelClasses = panelClasses.concat(...options.config.panelClass);
        }
        options.config.panelClass = panelClasses;
        options.config.maxHeight = 'unset';
        options.config.maxWidth = 'unset';
        return this.openInternal<TData, TRes>(FiDialogsCommonTwoButtonsComponent, options);
    }

    custom<TData, TRes>(options: IFiDialogOptions<TData, TRes>): IFiDialogResult<TRes> {
        if (!options.yesButtonText) {
            options.yesButtonText = this.appTextService.get('ok');
        }
        if (!options.noButtonText) {
            options.noButtonText = this.appTextService.get('common.cancel');
        }
        return this.openInternal<TData, TRes>(FiDialogsCommonTwoButtonsComponent, options);
    }

    /**
     * Method attach listeners to mat dialog to be closed on browser "back" button
     * @param modalRef
     * @param data
     * @param title
     * @param url
     */
    attachStateFullMatDialog(modalRef: MatDialogRef<any>, data?, title?, url?) {
        let closed = false;
        (function (onpopstate) {
            const onClose = modalRef.afterClosed().subscribe(() => {
                closed = true;
                window.history.back();
            });

            window.onpopstate = function () {
                if (onClose) {
                    onClose.unsubscribe();
                }
                if (!closed) {
                    modalRef.close();
                }
                window.onpopstate = onpopstate;
            };
        })(window.onpopstate);

        window.history.pushState(data, title || document.title, url);
    }

    private openInternal<TData = unknown, TRes = unknown>(
        containerRef: ComponentType<FiDialogsContainerBase<TData, TRes>>,
        options: IFiDialogOptions<TData, TRes>,
    ): IFiDialogResult<TRes> {
        const currentUser = this.userDataService.getUserData();

        const dontShowDialog =
            currentUser && currentUser.UserSettings && currentUser.UserSettings[options.dontShowAgainProperty];

        // prepare mat config for dialog container
        const matDialogData: IFiDialogOptions<TData, TRes> = Object.assign({}, options);
        delete matDialogData.config;
        const matDialogConfig: MatDialogConfig<TData> = Object.assign(
            {
                maxWidth: !this.detectDevices.isMobilePhone() ? '33%' : '90%',
                minWidth: !this.detectDevices.isMobilePhone() ? '25%' : '50%',
                maxHeight: '90%',
                minHeight: !this.detectDevices.isMobilePhone() ? '16%' : '20%',
            },
            options.config,
            { data: matDialogData },
        );

        // open dialog only if user previously not choosen 'don't show it again'
        let dialogRef: MatDialogRef<any, IFiDialogResultValue<TRes>>;
        if (!dontShowDialog) {
            dialogRef = this.matDialog.open(containerRef, matDialogConfig);
        }

        // prepare dialog result
        const dialogResult = {
            afterClosed: (callback: (dialogResult: IFiDialogResultValue<TRes>) => void) => {
                if (dialogRef) {
                    dialogRef.afterClosed().subscribe((result) => {
                        callback({
                            value: result ? result.value : null,
                            reason: result ? result.reason : FiDialogCloseReason.NoButton,
                            isDontShowAgain: result ? result.isDontShowAgain : false,
                        });
                    });
                } else {
                    callback({
                        reason: FiDialogCloseReason.DontShowAgain,
                    });
                }
                return dialogResult;
            },
            onYesButtonClick: (callback: (value: TRes) => void) => {
                if (dialogRef) {
                    dialogRef
                        .afterClosed()
                        .pipe(take(1))
                        .subscribe((result) => {
                            if (result && result.reason === FiDialogCloseReason.YesButton) {
                                callback(result ? result.value : null);
                            }
                        });
                }
                return dialogResult;
            },
            onNoButtonClick: (callback: () => void) => {
                if (dialogRef) {
                    dialogRef.afterClosed().subscribe((result) => {
                        if (!result || result.reason === FiDialogCloseReason.NoButton) {
                            callback();
                        }
                    });
                }
                return dialogResult;
            },
            close: () => {
                if (dialogRef) {
                    dialogRef.close();
                }
            },
            dialogRef: dialogRef,
        };

        dialogResult.afterClosed((result) => {
            if (result.isDontShowAgain) {
                this.fiBackend
                    .fetchDataPost(
                        'Core/User/SetUserSetting?userSettingType=' + encodeURIComponent(options.dontShowAgainProperty),
                    )
                    .then(() => {
                        currentUser.UserSettings[options.dontShowAgainProperty] = true;
                    });
            }
        });

        return dialogResult;
    }
}
